'use client';

import { useMemo } from 'react';
import type { CryptoDetail, CryptoTokenBase } from '@/type/crypto';
import { formatCompactValue } from '@/lib/utils/number';
import { useTranslation } from '@/lib/i18n';

interface CryptoOverviewProps {
  data?: Partial<CryptoDetail> | Partial<CryptoTokenBase>;
}

interface MetricItem {
  label: string;
  value: string;
}

export default function CryptoCapitalOverview({ data }: CryptoOverviewProps) {
  const { t } = useTranslation();
  const metrics = useMemo<MetricItem[]>(
    () => [
      {
        label: t('Market cap'),
        value: formatCompactValue(data?.market_cap, '$'),
      },
      {
        label: t('24h volume'),
        value: formatCompactValue(data?.volume_24h, '$'),
      },
      {
        label: t('Total supply'),
        value: formatCompactValue(data?.total_supply, '$'),
      },
      {
        label: t('Circulating supply'),
        value: formatCompactValue(data?.circulating_supply, '$'),
      },
    ],
    [
      data?.circulating_supply,
      data?.market_cap,
      data?.max_supply,
      data?.total_supply,
      data?.volume_24h,
      t,
    ],
  );

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className="h-18.5 flex flex-col justify-around rounded-lg border border-white/15 bg-black px-3 py-2"
        >
          <div className="text-sm text-[#757575]">{metric.label}</div>
          <div className="text-xl text-white">{metric.value}</div>
        </div>
      ))}
    </section>
  );
}
